import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'component-library';
import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import {
  PROTECTED_AREAS_IBAT_ORDER,
  PROTECTED_AREAS_OSM_ORDER,
  reverseProtectedAreas,
} from '@/api/rest/resources/types/protectedAreas';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { GraphFact } from '@/components/Charts/types';
import { useProtectedAreasMethodology } from '@/hooks/useProtectedAreasMethodology';
import { Area } from '@/utils/area';
import { isNullish } from '@/utils/booleans';

const UNITS = {
  distance: UnitEnum.km,
  area: UnitEnum['km^2'],
} as const;

export const ProtectedAreasTable = ({ data }: { data: PlotReportFactElement[] | GraphFact[] }) => {
  const { t } = useTranslation();

  const { hasIbat } = useProtectedAreasMethodology();
  const areaInBufferData = data.flatMap((item) => {
    if (item.name !== IndicatorName.protected_areas_area) {
      return [];
    }

    const protectionCategory = item.labels?.find(
      (label) => label.name === PlotReportFactLabelNameEnum.protection_category,
    )?.value as keyof typeof reverseProtectedAreas | undefined;

    if (!protectionCategory) {
      return [];
    }

    const category = reverseProtectedAreas[`${protectionCategory}`];

    if (!category || typeof item.value !== 'number') {
      return [];
    }

    return {
      category,
      area_in_buffer: parseFloat(
        new Area({
          value: item.value,
          dimension: 'squareMeters',
        })
          .toSquareKilometers()
          .value.toFixed(2),
      ),
    };
  });

  const nearestDistanceData = data.flatMap((item) => {
    if (item.name !== IndicatorName.protected_areas_min_distance) {
      return [];
    }

    const protectionCategory = item.labels?.find(
      (label) => label.name === PlotReportFactLabelNameEnum.protection_category,
    )?.value as keyof typeof reverseProtectedAreas | undefined;

    if (!protectionCategory) {
      return [];
    }

    const category = reverseProtectedAreas[`${protectionCategory}`];

    if (!category || typeof item.value !== 'number') {
      return [];
    }

    const nearestDistance = item.unit === UnitEnum.m ? item.value / 1000 : item.value;

    return {
      category,
      nearest_distance: parseFloat(nearestDistance.toFixed(2)),
    };
  });

  const orderedCategories = (hasIbat ? PROTECTED_AREAS_IBAT_ORDER : PROTECTED_AREAS_OSM_ORDER).flatMap(
    (osm) => reverseProtectedAreas[`${osm}`] ?? [],
  );

  const formattedData = orderedCategories.flatMap((category) => {
    const areaInBuffer = areaInBufferData.find((item) => item.category === category)?.area_in_buffer;
    const nearestDistance = nearestDistanceData.find((item) => item.category === category)?.nearest_distance;

    if (isNullish(areaInBuffer) && isNullish(nearestDistance)) {
      return [];
    }

    const areaCategory = t(`global.protectedAreas.categories.${category}`);
    const areaCategoryDescription = t(`global.protectedAreas.description.${category}`);

    return {
      category_label: `${areaCategory} - ${areaCategoryDescription}`,
      area_in_buffer: areaInBuffer ?? '--',
      nearest_distance: nearestDistance ?? '--',
    };
  });

  const columns = [
    {
      accessorKey: 'category_label',
      header: t('shared.ncaDetail.details.protectedAreas.stats.header.table.category'),
    },
    {
      accessorKey: 'nearest_distance',
      header: t('shared.ncaDetail.details.protectedAreas.stats.header.table.nearestDistance', {
        unit: UNITS.distance,
      }),
    },
    {
      accessorKey: 'area_in_buffer',
      header: t('shared.ncaDetail.details.protectedAreas.stats.header.table.areaInBuffer', {
        unit: UNITS.area,
      }),
    },
  ];

  return (
    <Table data-testid='iucn-category-table' className='w-full'>
      <TableHeader className='block'>
        <TableRow className='table w-full table-fixed'>
          {columns.map((column) => (
            <TableHead key={column.accessorKey} className='text-center'>
              {column.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody className='block max-h-[300px] overflow-y-auto [scrollbar-gutter:stable]'>
        {formattedData.map((row, index) => (
          <TableRow key={index} className='table w-full table-fixed'>
            <Cell value={row.category_label} />
            <Cell value={row.nearest_distance} />
            <Cell value={row.area_in_buffer} />
          </TableRow>
        ))}
        {formattedData.length === 0 && (
          <TableRow className='table w-full table-fixed'>
            <td colSpan={columns.length} className='text-center typography-body2 text-text-secondary py-4'>
              {t('shared.ncaDetail.details.protectedAreas.stats.header.table.noData')}
            </td>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

const Cell = ({ value }: { value: string | number }) => {
  return <TableCell className='text-center'>{value}</TableCell>;
};
